// Transcript parsing for the meeting page (ORB-436). The stored transcript.json is an array of
// { sentence, startTime, endTime, speaker_name } rows (Webster's shape). Other sources vary in key
// casing, so match keys case-insensitively — the same tolerance the backend chunker applies.

export interface TranscriptLine {
  /** Seconds from the start of the recording (maps directly onto HTMLMediaElement.currentTime). */
  start: number
  end: number
  speaker: string
  text: string
}

const firstString = (...values: unknown[]): string => {
  for (const value of values) if (typeof value === 'string') return value
  return ''
}

// A Fireflies export writes "01:23"; parseFloat reads that as 1, so a clock is summed part by part.
// Uploads are stored as seconds since ORB-959; this keeps transcripts stored before then readable.
const CLOCK = /^\d+(:\d{1,2}){1,2}(\.\d+)?$/

export const toSeconds = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (CLOCK.test(trimmed)) return trimmed.split(':').reduce((total, part) => total * 60 + parseFloat(part), 0)
    const parsed = parseFloat(trimmed)
    return Number.isFinite(parsed) ? parsed : NaN
  }
  return NaN
}

/**
 * Parse the transcript payload into per-sentence lines that KEEP their timing. Unlike the old reader we
 * do not collapse consecutive same-speaker rows: each sentence is its own line so it can be a highlight
 * target and a click-to-seek anchor. Returns null when the payload isn't a recognizable transcript array.
 */
export function parseTranscriptLines(raw: unknown): TranscriptLine[] | null {
  if (!Array.isArray(raw)) return null
  const lines: TranscriptLine[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    // Index keys by lowercased name so lookups tolerate camel/snake/Pascal variants. A Map (not a plain
    // object) keeps a stray "__proto__"/"constructor" key in the payload inert.
    const lc = new Map<string, unknown>()
    for (const [key, value] of Object.entries(item as Record<string, unknown>)) lc.set(key.toLowerCase(), value)
    const text = firstString(lc.get('sentence'), lc.get('text')).trim()
    if (!text) continue
    const speaker = firstString(lc.get('speaker_name'), lc.get('speakername'), lc.get('speaker')) || 'Speaker'
    const start = toSeconds(lc.get('starttime') ?? lc.get('start') ?? lc.get('start_time'))
    const end = toSeconds(lc.get('endtime') ?? lc.get('end') ?? lc.get('end_time'))
    lines.push({ start, end, speaker, text })
  }
  return lines
}

/**
 * The index of the line covering `currentTime` — the last line whose start is at or before it — or -1
 * before the first line. Assumes lines are in chronological order (they are). Lines with unparseable
 * (NaN) start times are skipped so they never become the active line.
 */
export function activeLineIndex(lines: TranscriptLine[], currentTime: number): number {
  let index = -1
  for (let i = 0; i < lines.length; i++) {
    const start = lines[i].start
    if (!Number.isFinite(start)) continue
    if (start <= currentTime) index = i
    else break
  }
  return index
}

/** Seconds → "m:ss", or "h:mm:ss" once past an hour. */
export function formatClock(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00'
  const seconds = Math.floor(totalSeconds % 60)
  const minutes = Math.floor((totalSeconds / 60) % 60)
  const hours = Math.floor(totalSeconds / 3600)
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  const ss = String(seconds).padStart(2, '0')
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
}
